import { getSpeechRecognitionIfSupported } from "@jugl-web/utils/speechRecognition";
import type { VoiceModeFatalErrorReason } from "./types";

interface VoiceModeSpeechRecognitionOptions {
  language?: string;
  onUserSpeechTranscript?: (transcript: string) => void;
  onUserSpeechFinalTranscript?: (transcript: string) => void;
  onFatalError?: (reason: VoiceModeFatalErrorReason) => void;
}

const DEFAULT_LANGUAGE = "en-US";

/**
 * Handles continuous speech recognition in the browser and provides callbacks for transcripts and errors.
 */
export class VoiceModeSpeechRecognition {
  private readonly recognition: SpeechRecognition;

  private readonly onUserSpeechTranscript: (transcript: string) => void;
  private readonly onUserSpeechFinalTranscript: (transcript: string) => void;
  private readonly onFatalError: (reason: VoiceModeFatalErrorReason) => void;

  private readonly handleResult: (event: SpeechRecognitionEvent) => void;
  private readonly handleEnd: () => void;
  private readonly handleError: (event: SpeechRecognitionErrorEvent) => void;

  private isActive = false;
  private isDisposed = false;

  constructor({
    language = DEFAULT_LANGUAGE,
    onUserSpeechTranscript,
    onUserSpeechFinalTranscript,
    onFatalError,
  }: VoiceModeSpeechRecognitionOptions) {
    const recognition = getSpeechRecognitionIfSupported();

    if (!recognition) {
      throw new Error("Speech recognition not supported");
    }

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language;
    recognition.maxAlternatives = 1;

    this.recognition = recognition;
    this.onUserSpeechTranscript = onUserSpeechTranscript ?? noop;
    this.onUserSpeechFinalTranscript = onUserSpeechFinalTranscript ?? noop;
    this.onFatalError = onFatalError ?? noop;

    // Stable listener references for add/removeEventListener pairing.
    this.handleResult = this.onRecognitionResult.bind(this);
    this.handleEnd = this.onRecognitionEnd.bind(this);
    this.handleError = this.onRecognitionError.bind(this);

    recognition.addEventListener("result", this.handleResult);
    recognition.addEventListener("end", this.handleEnd);
    recognition.addEventListener("error", this.handleError);
  }

  /**
   * Forwards interim and final transcripts to registered callbacks.
   */
  private onRecognitionResult(event: SpeechRecognitionEvent): void {
    const result = event.results.item(event.resultIndex);
    const alternative = result.item(0);
    const normalizedTranscript = alternative.transcript.trim();

    if (normalizedTranscript.length === 0) {
      return;
    }

    if (result.isFinal) {
      this.onUserSpeechFinalTranscript(normalizedTranscript);
    }

    this.onUserSpeechTranscript(normalizedTranscript);
  }

  /**
   * Starts recognition again if it ends, unless stopped or disposed.
   */
  private onRecognitionEnd(): void {
    if (!this.isActive || this.isDisposed) {
      return;
    }

    this.startRecognition();
  }

  /**
   * Stops auto-restart on fatal errors; notifies consumers with a typed reason.
   */
  private onRecognitionError(event: SpeechRecognitionErrorEvent): void {
    const reason = mapSpeechRecognitionErrorToFatalReason(event.error);

    if (!reason) {
      return;
    }

    this.isActive = false;
    this.onFatalError(reason);
  }

  /**
   * Starts the native recognition engine (unless already running).
   */
  private startRecognition(): void {
    try {
      this.recognition.start();
    } catch {
      // InvalidStateError when recognition is already running — safe to ignore.
    }
  }

  /**
   * Begins continuous listening. No-ops when already active.
   */
  public start(): void {
    if (this.isDisposed) {
      throw new Error("VoiceModeSpeechRecognition has been disposed");
    }

    if (this.isActive) {
      return;
    }

    this.isActive = true;
    this.startRecognition();
  }

  /**
   * Stops listening without disposing the instance (can be started again).
   */
  public stop(): void {
    this.isActive = false;

    try {
      this.recognition.stop();
    } catch {
      // Recognition may already be stopped.
    }
  }

  /**
   * Releases recognition and listeners so the instance can be dropped.
   */
  public dispose(): void {
    if (this.isDisposed) {
      return;
    }

    this.isDisposed = true;
    this.isActive = false;

    this.recognition.removeEventListener("result", this.handleResult);
    this.recognition.removeEventListener("end", this.handleEnd);
    this.recognition.removeEventListener("error", this.handleError);

    try {
      this.recognition.abort();
    } catch {
      // Recognition may already be stopped.
    }
  }
}

/**
 * Maps Web Speech API error codes to session-level fatal reasons.
 * Returns null for recoverable / non-fatal errors (e.g. no-speech, aborted).
 */
const mapSpeechRecognitionErrorToFatalReason = (
  error: SpeechRecognitionErrorCode
): VoiceModeFatalErrorReason | null => {
  if (error === "not-allowed") {
    return "microphone-permission-denied";
  }

  if (error === "service-not-allowed") {
    return "speech-recognition-service-not-allowed";
  }

  if (error === "network") {
    return "network";
  }

  return null;
};

const noop = () => {};
